import React, { Component, PureComponent } from "react";
import { debounce, sortBy } from "lodash";
import createDisplayObject from "./BaseDisplayObject.js";
import DragManager from "./DragManager.js";
import LayoutManager from "./LayoutManager.js";
import PropTypes from "prop-types";
import WebWorkers from "WebWorkers/WebWorkers";
import SortItemsToRender from "WebWorkers/Workers/SortItemsToRender";
import memoize from "memoize-one";

export default function createAbsoluteGrid(
	DisplayObject,
	displayProps = {},
	forceImpure = false
) {
	const Comp = forceImpure ? Component : PureComponent;
	const WrappedDisplayObject = createDisplayObject(
		DisplayObject,
		displayProps,
		forceImpure
	);

	return class extends Comp {
		static defaultProps = {
			items: [],
			keyProp: "key",
			filterProp: "filtered",
			sortProp: "sort",
			itemWidth: 128,
			itemHeight: 128,
			verticalMargin: -1,
			responsive: false,
			dragEnabled: false,
			animation: "transform 300ms ease",
			zoom: 1,
			rtl: false,
			minSort: 0,
			maxSort: 9999,
			onMove: () => {},
			onDragStart: () => {},
			onDragMove: () => {},
			onDragEnd: () => {}
		};

		static propTypes = {
			items: PropTypes.arrayOf(PropTypes.object).isRequired,
			itemWidth: PropTypes.number,
			itemHeight: PropTypes.number,
			verticalMargin: PropTypes.number,
			zoom: PropTypes.number,
			containerZoom: PropTypes.number,
			responsive: PropTypes.bool,
			dragEnabled: PropTypes.bool,
			keyProp: PropTypes.string,
			sortProp: PropTypes.string,
			filterProp: PropTypes.string,
			animation: PropTypes.string,
			onMove: PropTypes.func,
			onDragStart: PropTypes.func,
			onDragMove: PropTypes.func,
			onDragEnd: PropTypes.func,
			rtl: PropTypes.bool,
			minSort: PropTypes.number,
			maxSort: PropTypes.number,
			forceResize: PropTypes.bool,
			forceResizeDone: PropTypes.func
		};

		constructor(props, context) {
			super(props, context);
			this.onResize = debounce(this.onResize, 150);
			this.dragManager = new DragManager(
				this.props.onMove,
				this.props.onDragStart,
				this.props.onDragEnd,
				this.props.onDragMove,
				this.props.keyProp,
				this.setDragItem.bind(this)
			);
			this.state = {
				layoutWidth: 0,
				parentNode: null,
				sortedIndex: {},
				render: false,
				scrollTop: 0,
				dragItem: null
			};

			this.dragItem = null;
			this.scrollTopCounter = 0;
			this.SortItemsToRenderTimeout = null;
			this.updateScrollTop = this.updateScrollTop.bind(this);
			this.setTopScroll = this.setTopScroll.bind(this);
		}

		setDragItem(dragItem) {
			this.setState({ dragItem });
		}

		filterSort = (list, minSort, maxSort) => {
			return list.filter(
				item =>
					item.sort >= minSort &&
					item.sort < maxSort &&
					item.disabled != true
			);
		};

		filterDisabled = list => {
			return list.filter(item => item.disabled != true);
		};

		getHorizontalMargin = memoize(
			(itemsPerRow, verticalMargin, layoutWidth, itemWidth) => {
				return itemsPerRow === 1
					? verticalMargin
					: Math.round(layoutWidth - itemsPerRow * itemWidth) /
							(itemsPerRow - 1);
			}
		);

		scrollingItemsToRender() {
			if (this.props.gridBoxContainer == null)
				return { startItem: 0, endItem: 0 };
			const containerHeight = this.props.gridBoxContainer.clientHeight;
			const itemsPerRow = this.props.itemsPerRow;
			const horizontalMargin = this.getHorizontalMargin(
				itemsPerRow,
				this.props.verticalMargin,
				this.state.layoutWidth,
				this.props.itemWidth
			);
			const verticalMargin =
				this.props.verticalMargin === -1
					? horizontalMargin
					: this.props.verticalMargin;

			const itemHeight =
				(this.props.itemHeight + verticalMargin) *
				this.props.containerZoom;
			const startHeight = this.state.scrollTop;
			const endHeight = startHeight + containerHeight;
			const startItem = Math.max(
				0,
				Math.floor(startHeight / itemHeight) * itemsPerRow - itemsPerRow
			);
			const endItem =
				Math.ceil(endHeight / itemHeight) * itemsPerRow + itemsPerRow;
			return { startItem: startItem, endItem: endItem };
		}

		render() {
			if (!this.state.layoutWidth || !this.props.items.length) {
				return <div ref={node => (this.container = node)} />;
			}

			let filteredItems = [];
			const itemsToRender = this.scrollingItemsToRender();
			if (
				this.props.minSort == null ||
				this.props.maxSort == null ||
				(this.props.minSort == 0 && this.props.maxSort == 9999)
			)
				if (this.props.gridName != "search")
					filteredItems = this.props.items;
				else filteredItems = this.filterDisabled(this.props.items);
			else
				filteredItems = this.filterSort(
					this.props.items,
					this.props.minSort,
					this.props.maxSort
				);

			const startSort = itemsToRender.startItem + this.props.minSort;
			const endSort = itemsToRender.endItem + this.props.minSort;

			const filteredItemsRender = filteredItems.filter(
				item =>
					(item.sort >= startSort && item.sort < endSort) ||
					(this.state.dragItem && this.state.dragItem.key == item.key)
			);

			let filteredIndex = itemsToRender.startItem;

			sortBy(filteredItemsRender, this.props.sortProp).forEach(item => {
				if (!item[this.props.filterProp]) {
					const key = item[this.props.keyProp];
					if (
						this.state.dragItem &&
						item.key == this.state.dragItem.key &&
						(item.sort < startSort || item.sort >= endSort)
					) {
						this.state.dragItem = item;
						item.index = item.sort;
						item.dragInProgress = true;
					} else {
						item.dragInProgress = false;
						item.index = filteredIndex;
						filteredIndex++;
					}
				}
			});

			return this.renderItems(filteredItemsRender, filteredItems.length);
		}

		renderItems(items, filteredItemsLength) {
			const gridItems = items.map(item => {
				const key = item[this.props.keyProp];
				return (
					<WrappedDisplayObject
						item={item}
						index={item.index}
						key={key}
						itemsLength={filteredItemsLength}
						animation={this.props.animation}
						itemWidth={this.props.itemWidth}
						itemHeight={this.props.itemHeight}
						layoutWidth={this.state.layoutWidth}
						verticalMargin={this.props.verticalMargin}
						zoom={this.props.zoom}
						containerZoom={this.props.containerZoom}
						keyProp={this.props.keyProp}
						filterProp={this.props.filterProp}
						dragEnabled={this.props.dragEnabled}
						dragManager={this.dragManager}
						container={this.state.container}
						rtl={this.props.rtl}
						isCtrlKeyDown={this.props.isCtrlKeyDown}
						imageMode={this.props.imageMode}
					/>
				);
			});

			const options = {
				itemWidth: this.props.itemWidth,
				itemHeight: this.props.itemHeight,
				verticalMargin: this.props.verticalMargin,
				zoom: this.props.zoom
			};
			const layout = new LayoutManager(options, this.state.layoutWidth);
			const gridStyle = {
				position: "relative",
				display: "block",
				height: layout.getTotalHeight(filteredItemsLength)
			};

			return (
				<div
					style={gridStyle}
					className="absoluteGrid"
					ref={node => (this.container = node)}
				>
					{gridItems}
				</div>
			);
		}

		componentWillMount() {}

		componentDidMount() {
			//If responsive, listen for resize
			if (this.props.responsive) {
				window.addEventListener("resize", this.onResize);
			}
			this.onResize();
			this.setContainer();
		}

		componentWillUnmount() {
			if (this.props.gridBoxContainer) {
				this.props.gridBoxContainer.removeEventListener(
					"scroll",
					this.updateScrollTop,
					this.getSupportsPassive() ? { passive: true } : false
				);
			}
		}

		getSupportsPassive() {
			var supportsPassive = false;
			try {
				var opts = Object.defineProperty({}, "passive", {
					get: function() {
						supportsPassive = true;
					}
				});
				window.addEventListener("testPassive", null, opts);
				window.removeEventListener("testPassive", null, opts);
			} catch (e) {}

			return supportsPassive;
		}

		updateScrollTop = event => {
			this.scrollTop = event.target.scrollTop;
			console.log(
				this.scrollTopCounter + " ) scrollTop: " + this.scrollTop
			);
			this.scrollTopCounter++;
			clearTimeout(this.SortItemsToRenderTimeout);
			this.SortItemsToRenderTimeout = setTimeout(
				this.setTopScroll,
				this.state.dragItem ? 40 : 50
			);
		};

		setTopScroll() {
			this.scrollTopCounter = 0;
			this.setState({ scrollTop: this.scrollTop });
		}

		componentDidUpdate(prevProps, prevState) {
			if (
				prevProps.forceResize != this.props.forceResize &&
				this.props.forceResize == true
			) {
				this.onResize();
			}

			if (this.props.forceRender == true && this.props.forceRenderDone)
				this.props.forceRenderDone();

			if (
				this.props.gridBoxContainer &&
				prevProps.gridBoxContainer != this.props.gridBoxContainer
			) {
				if (prevProps.gridBoxContainer != null) {
					this.props.gridBoxContainer.removeEventListener(
						"scroll",
						this.updateScrollTop,
						this.getSupportsPassive() ? { passive: true } : false
					);
				}
				this.props.gridBoxContainer.addEventListener(
					"scroll",
					this.updateScrollTop,
					this.getSupportsPassive() ? { passive: true } : false
				);
			}
		}

		componentWillUnmount() {
			window.removeEventListener("resize", this.onResize);
		}

		getContainerParentNode() {
			if (this.container != null && this.container.parentNode != null)
				return this.container.parentNode;
			else return null;
		}

		setContainer() {
			if (this.container) {
				this.setState({ container: this.container });
			} else {
				setTimeout(() => {
					if (this.container) {
						this.setState({ container: this.container });
					}
				}, 100);
			}
		}

		onResize = () => {
			if (window.requestAnimationFrame) {
				window.requestAnimationFrame(this.getDOMWidth);
			} else {
				setTimeout(this.getDOMWidth, 66);
			}
		};

		getDOMWidth = () => {
			const width = this.container && this.container.clientWidth;
			if (this.state.layoutWidth !== width) {
				this.setState({ layoutWidth: width }, () => {
					if (this.props.onLayoutWidth)
						this.props.onLayoutWidth(width);
					if (this.props.forceResizeDone)
						this.props.forceResizeDone();
				});
			}
		};

		compareObjects(obj1, obj2, checkLength = false) {
			if (
				(obj1 != null && obj2 == null) ||
				(obj1 == null && obj2 != null)
			)
				return true;
			let keys = Object.keys(obj1);
			if (checkLength) {
				if (keys.length != Object.keys(obj2).length) return true;
			}
			for (let i = 0; i < keys.length; i++) {
				let key = keys[i];
				if (
					obj1[key] !== null &&
					typeof obj1[key] === "object" &&
					obj2[key] !== null &&
					typeof obj2[key] === "object"
				) {
					if (this.compareObjects(obj1[key], obj2[key], true))
						return true;
				} else if (obj1[key] != obj2[key]) return true;
			}
			return false;
		}
	};
}
